import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icon } from 'js/components';
import { faFaceThinking } from '@fortawesome/pro-regular-svg-icons';

import styles from './OrgAnalyze.module.scss';

const GUTTER = 12;
const MAX_WIDTH = 48 * 16;

function fitMenu(menu, trigger) {
  if (!menu || !trigger) {
    return;
  }

  const rect = trigger.getBoundingClientRect();
  const viewport = window.innerWidth;
  const maxWidth = Math.min(MAX_WIDTH, viewport - GUTTER * 2);

  menu.style.position = 'fixed';
  menu.style.top = 'auto';
  menu.style.bottom = `${window.innerHeight - rect.top + 6}px`;
  menu.style.width = 'max-content';
  menu.style.maxWidth = `${maxWidth}px`;
  menu.style.minWidth = `${Math.min(18 * 16, maxWidth)}px`;

  const width = Math.min(menu.getBoundingClientRect().width, maxWidth);
  let left = rect.left;
  if (left + width > viewport - GUTTER) {
    left = viewport - GUTTER - width;
  }
  if (left < GUTTER) {
    left = GUTTER;
  }

  menu.style.left = `${left}px`;
  menu.style.right = 'auto';
}

export default function AskNext({
  items,
  onPick,
  disabled = false,
  variant = 'chip',
}) {
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }

    const update = () => fitMenu(menuRef.current, triggerRef.current);
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, items]);

  if (!items?.length) {
    return null;
  }

  return (
    <div className={styles.askNext} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={variant === 'icon' ? styles.actionIcon : styles.actionChip}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Questions you could ask next"
        title="Ask next"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
      >
        <Icon icon={faFaceThinking} />
        {variant === 'chip' ? <span>Ask next</span> : null}
      </button>
      {open ? (
        <div ref={menuRef} className={styles.askNextMenu} role="menu">
          {items.map((question) => (
            <button
              key={question.question}
              type="button"
              className={styles.suggestion}
              role="menuitem"
              onClick={() => {
                onPick(question.question);
                setOpen(false);
              }}
            >
              <span>{question.question}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
